import { useState, useEffect, useCallback } from "react";
import Layout from "../components/public/Layout";
import BookingSteps from "../components/public/BookingSteps";
import ReservePage from "../components/public/ReservePage";
import DateTimePage from "../components/public/DateTimePage";
import BookPage from "../components/public/BookPage";
import PaymentPage from "../components/public/PaymentPage";
import ConfirmPage from "../components/public/ConfirmPage";
import styles from "../styles/public/booking-page.module.css";
import '@fontsource/poppins'; // Defaults to weight 400
import api from "../services/api";
import { Alert, Container } from "reactstrap";

const BookingPage = () => {
  const [organization, setOrganization] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isCreatingHold, setIsCreatingHold] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingData, setBookingData] = useState({
    service: {},
    date: null,
    time: null,
    contactInfo: null,
    organization: process.env.REACT_APP_CURRENT_ORGANIZATION_ID,
    eventId: null,
    eventGuid: null,
    eventStart: null,
    eventEnd: null,
    price: 0,
  });

  useEffect(() => {
    document.title = 'Reserve2Day';

    api.get("/get-org-details/")
      .then((response) => {
        setOrganization(response.data);
      });
  }, []); // <-- This should be outside the .then()

  const handleNext = (data) => {
    setBookingData((prev) => ({ ...prev, ...data }));
    setCurrentStep((prev) => prev + 1);
    setIsTimeout(false);
    setBookingError('');
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Don't cancel the hold when navigating between BookPage (3) and PaymentPage (4)
      // The hold should remain active so user can complete payment
      if (currentStep !== 4) {
        cancelHold(false);
      }
      handleEditStep(currentStep - 1);
      setIsTimeout(false);
    }
  };

  const handleContinue = async () => {
    if (currentStep === 2 && bookingData.date && bookingData.time) {
      handleNext({ date: bookingData.date, time: bookingData.time });
    } else if (
      currentStep === 1 &&
      Object.keys(bookingData.service).length > 0
    ) {
      handleNext({ service: bookingData.service });
    } else if (currentStep === 3 && bookingData.contactInfo) {
      // Check if service requires payment
      const requiresPayment = bookingData.service?.payment_option &&
        bookingData.service.payment_option !== 'None';

      if (requiresPayment) {
        // Don't book yet, just save contact info and calculate total
        if (await updateEventContactInfo()) {
          handleNext({ contactInfo: bookingData.contactInfo });
        }
      } else {
        // No payment required, book immediately
        if (await updateEvent()) {
          handleNext({ contactInfo: bookingData.contactInfo });
        }
      }
    }
  };

  const handleEditStep = (step) => {
    setCurrentStep(step);
    window.scrollTo(0, 0);

    if (step === 1) {
      // Going back to Reserve — clear everything
      setBookingData((prev) => ({
        ...prev,
        service: {},
        date: null,
        time: null,
      }));
    } else if (step === 2) {
      // Going back to DateTime — keep date, clear time only
      setBookingData((prev) => ({
        ...prev,
        time: null,
      }));
    }
    setIsTimeout(false);
  };

  const isContinueDisabled = () => {
    switch (currentStep) {
      case 1:
        return Object.keys(bookingData.service).length === 0;
      case 2:
        return !bookingData.date || !bookingData.time;
      case 3:
        const usPhoneRegex = /^(\+1\s?)?(\([2-9][0-9]{2}\)|[2-9][0-9]{2})[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/;
        const isValidPhone = usPhoneRegex.test(bookingData.contactInfo?.phone);
        const isValidEmail = /\S+@\S+\.\S+/.test(bookingData.contactInfo?.email);

        const disabled = !(
          bookingData.contactInfo?.firstName?.trim() &&
          bookingData.contactInfo?.lastName?.trim() &&
          bookingData.contactInfo?.email?.trim() &&
          isValidEmail &&
          bookingData.contactInfo?.phone?.trim() &&
          isValidPhone
        );
        return disabled;
      case 4:
        return false;
      case 5:
        return false;
      default:
        return true;
    }
  };

  const getContinueText = () => {
    return currentStep === 3 ? "Book your reservation" : "Continue";
  };

  const updateBookingData = (data) => {
    setBookingData((prev) => ({ ...prev, ...data }));
  };

  const updateEventContactInfo = async () => {
    try {
      const response = await api.put(`/book/${bookingData.eventGuid}/`, {
        customer_name: `${bookingData.contactInfo.firstName} ${bookingData.contactInfo.lastName}`,
        customer_phone: bookingData.contactInfo.phone,
        customer_email: bookingData.contactInfo.email,
        first_time_user: bookingData.contactInfo.isFirstTime,
        marketing_opt_in: bookingData.contactInfo.wantsEmails,
        notes: bookingData.contactInfo.notes,
        start: bookingData.eventStart,
        end: bookingData.eventEnd,
        resource: bookingData.resource,
        service: bookingData.service.id,
        discount_code: bookingData.contactInfo.discountCode,
        sendEmail: false, // Don't send email yet
        state: 'Hold' // Keep as Hold until payment
      });

      setBookingData((prev) => ({
        ...prev,
        total: response.data.total,
      }));

      return true;
    } catch (error) {
      console.log("Error updating event contact info:", error);
      if (error.response?.data) {
        const errorMsg = error.response.data.detail || 'An error occurred while updating your booking';
        setBookingError(errorMsg);
        setIsTimeout(false);
        cancelHold(false);
        setCurrentStep(1);
      }
      return false;
    }
  };

  const updateEvent = async () => {
    try {
      const response = await api.put(`/book/${bookingData.eventGuid}/`, {
        customer_name: `${bookingData.contactInfo.firstName} ${bookingData.contactInfo.lastName}`,
        customer_phone: bookingData.contactInfo.phone,
        customer_email: bookingData.contactInfo.email,
        first_time_user: bookingData.contactInfo.isFirstTime,
        marketing_opt_in: bookingData.contactInfo.wantsEmails,
        notes: bookingData.contactInfo.notes,
        start: bookingData.eventStart,
        end: bookingData.eventEnd,
        resource: bookingData.resource,
        service: bookingData.service.id,
        discount_code: bookingData.contactInfo.discountCode,
        sendEmail: true,
        state: 'Booked'
      });

      setBookingData((prev) => ({
        ...prev,
        total: response.data.total,
      }));

      // console.log("Event update response:", response);
      return true;
    } catch (error) {
      console.log("Error updating event:", error);
      if (error.response?.data) {
        const errorMsg = error.response.data.detail || 'An error occurred while booking your reservation';
        setBookingError(errorMsg);
        setIsTimeout(false);
        cancelHold(false);
        setCurrentStep(1);
      }
      return false;
    }
  };

  const handleTimeSelected = async (time, price) => {
    setIsCreatingHold(true);
    try {
      const startDate = new Date(bookingData.date);

      const [timePart, amPm] = time.split(' ');
      const [hours, minutes] = timePart.split(':').map(Number);

      let startHours;
      if (amPm.toLowerCase() === 'pm' && hours !== 12) {
        startHours = hours + 12;
      } else if (amPm.toLowerCase() === 'am' && hours === 12) {
        startHours = 0;
      } else {
        startHours = hours;
      }

      startDate.setHours(startHours);
      startDate.setMinutes(minutes);
      startDate.setSeconds(0);

      const durationInMinutes = bookingData.service.duration;

      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + durationInMinutes);

      const eventData = {
        service: bookingData.service.id,
        customer_name: "Online Hold",
        customer_email: "",
        description: '',
        notes: '',
        first_time_user: false,
        marketing_opt_in: false,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        resource: null,
        resourceLocked: false,
        isAllDay: false,
        price: price || 0,
        state: 'Hold',
        source: 'Online'
      };

      const response = await api.post('/hold/', eventData);
      // console.log("Event creation response:", response);

      const eventId = response.data.id;
      const eventGuid = response.data.guid;
      const eventStart = response.data.start;
      const eventEnd = response.data.end;
      const eventPrice = response.data.price;
      updateBookingData({ eventId, eventGuid, eventStart, eventEnd, eventPrice });

      setCurrentStep(3);
      return true;
    } catch (error) {
      console.log(error.message);
      if (error.response?.data) {
        const errorMsg = error.response.data.detail || 'An error occurred while creating your booking hold';
        setBookingError(errorMsg);
        setIsTimeout(false);
        setCurrentStep(1);
      }
      return false;
    } finally {
      setIsCreatingHold(false);
    }
  };

  const cancelHold = useCallback((isTimeoutCancel = false) => {
    if (!bookingData.eventGuid) return;

    api.get(`/cancel-hold/?event=${bookingData.eventGuid}`)
      .catch((err) => {
        console.error("Error cancelling booking:", err);
      });

    // // Cancel the payment intent
    // if (paymentIntentId) {
    //   api.post("/stripe/payment-intent/cancel/", {
    //     payment_intent_id: paymentIntentId,
    //   }).catch(err => console.error("Error canceling payment intent on timeout:", err));
    // }

    if (isTimeoutCancel) {
      setIsTimeout(true);
    }
    setCurrentStep(1);
    setBookingData((prev) => ({
      ...prev,
      eventId: null,
      eventGuid: null,
      eventStart: null,
      eventEnd: null,
      eventPrice: null
    })
    );
  }, [bookingData.eventGuid]);

    const renderStep = () => {
      const requiresPayment = bookingData.service?.payment_option &&
        bookingData.service.payment_option !== 'None';

      switch (currentStep) {
        case 1:
          return <ReservePage
            organization={organization}
            onNext={handleNext}
            bookingData={bookingData}
          />;
        case 2:
          return (
            <DateTimePage
              bookingData={bookingData}
              updateBookingData={updateBookingData}
              onEditStep={handleEditStep}
              onTimeSelected={handleTimeSelected}
              isCreatingHold={isCreatingHold}
              initialDate={new Date()}
              maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
              organization={organization}
            />
          );
        case 3:
          return (
            <BookPage
              onNext={handleNext}
              onBack={handleBack}
              bookingData={bookingData}
              onEditStep={handleEditStep}
              updateBookingData={updateBookingData}
              handleCancel={cancelHold}
              organization={organization}
            />
          );
        case 4:
          if (requiresPayment) {
            return (
              <PaymentPage
                bookingData={bookingData}
                onSuccess={handleNext}
                onBack={handleBack}
                onEditStep={handleEditStep}
                handleCancel={cancelHold}
                organization={organization}
              />
            );
          } else {
            return (
              <ConfirmPage
                bookingData={bookingData}
                organization={organization}
              />
            );
          }
        case 5:
          return (
            <ConfirmPage
              bookingData={bookingData}
              organization={organization}
            />
          );
        default:
          return null;
      }
    };

    // Calculate if payment is required based on selected service
    const requiresPayment = bookingData.service?.payment_option &&
      bookingData.service.payment_option !== 'None';
    const maxStep = requiresPayment && bookingData.total > 0 ? 5 : 4;

    return (
      <Layout
        organization={organization}
        showNavigation={currentStep > 1 && currentStep < maxStep && currentStep !== 4}
        navigationProps={{
          onClick: handleBack,
          handleContinue: handleContinue,
          disabled: isContinueDisabled(),
          continueText: getContinueText(),
          showContinueButton: currentStep !== 2 && currentStep !== 4,
          handleCancel: cancelHold,
        }}
      >
        <div className={styles["booking-page"]}>
          <BookingSteps
            currentStep={currentStep}
            onEditStep={handleEditStep}
            handleCancel={cancelHold}
            requiresPayment={requiresPayment}
          />
          {isTimeout && (
            <Container>
              <Alert color="warning" className="mt-3 text-center" fade={false}>
                Your booking has timed out. Please start a new booking.
              </Alert>
            </Container>
          )}
          {bookingError && (
            <Container>
              <Alert color="danger" className="mt-3 text-center" toggle={() => setBookingError('')} fade={false}>
                {bookingError}
              </Alert>
            </Container>
          )}
          {renderStep()}
        </div>
      </Layout>
    );
  };

  export default BookingPage;
